import createDOMPurify from 'dompurify';
import { AllHtmlEntities } from 'html-entities';
import $ from 'jquery';
import React from 'react';
// eslint-disable-next-line import/no-unresolved
import { Link } from 'react-router-dom';
import scrollIntoView from 'scroll-into-view-if-needed';
import useWindowSize from './useWindowSize';

const TIERS = ['xs', 'sm', 'md', 'lg', 'xl'];

const COMMON_CONSTANTS = {
  CARD_VARIANTS: {
    DEFAULT: 'default',
    FIXED_WIDTH: 'fixedWidth',
  },
  IMAGE_FIT: {
    CONTAIN: 'contain',
    COVER: 'cover',
    FILL: 'fill',
    INSIDE: 'inside',
    OUTSIDE: 'outside',
  },
  IMAGE_RATIOS: {
    FIVE_BY_FOUR: '5x4',
    TWO_BY_ONE: '2x1',
  },
};

const purifyDom = (html) => {
  const config = {
    ADD_ATTR: ['target'],
  };
  const domPurify = createDOMPurify(window);
  return { __html: domPurify.sanitize(html, config) };
};

const decodeHtmlEntities = (encodedString) => new AllHtmlEntities().decode(encodedString);

const prepareHtml = (html) => purifyDom(decodeHtmlEntities(html));

const toggleClass = (target, add, className = 'highlight') => {
  if (add) {
    target.classList.add(className);
  } else {
    target.classList.remove(className);
  }
};

// prettier-ignore
// eslint-disable-next-line react/prop-types
const LinkOrAnchor = ({ url, ...other }) => (
  // eslint-disable-next-line jsx-a11y/anchor-has-content, react/jsx-filename-extension, react/jsx-props-no-spreading
  /^https?:\/\//.test(url) ? <a {...other} href={url} /> : <Link {...other} to={url} />
);

const outlineElement = (target, add = true) => {
  toggleClass(target, add);
};

const dayCardOutlineElement = (target, add = true, number, length) => {
  toggleClass(target, add);

  // eslint-disable-next-line no-plusplus
  for (let i = 1; i <= length - 1; i++) {
    document.getElementsByClassName('align-items-start')[i].removeAttribute('tabindex');
  }
};

const outlineElementForSort = (target, add = false, className = 'sort__control--is-focused') => {
  toggleClass($(target), add, className);
};

const scrollToElement = (element, options) => {
  const defaultOptions = {
    behavior: 'smooth',
    block: 'nearest',
    inline: 'nearest',
    scrollMode: 'if-needed',
  };

  const scrollOptions = {
    ...defaultOptions,
    ...options,
  };

  scrollIntoView(element, scrollOptions);
};

const keyUpOutline = (event) => {
  if (event.keyCode === 9 || event.keyCode === 13 || event.keyCode === 32) {
    outlineElement(event.target);
  }
};

const dayCardKeyPressNavigate = (event, number, length) => {
  switch (event.keyCode) {
    case 9:
      outlineElement(event.target);
      break;
    case 32:
      outlineElement(event.target);
      break;
    case 13:
      // eslint-disable-next-line no-plusplus
      for (let i = 1; i <= length - 1; i++) {
        document.getElementsByClassName('align-items-start')[i].setAttribute('tabindex', '-1');
      }
      break;
    default:
      break;
  }
};

const getAccessibilityFunctions = ({ hasOutlineOnBlur, hasOutlineOnKeyUp, onBlur, onKeyUp } = {}) => ({
  onBlur: (e) => {
    if (hasOutlineOnBlur) {
      outlineElement(e.target, false);
    }
    if (onBlur) {
      onBlur(e);
    }
  },
  onKeyUp: (e) => {
    if (hasOutlineOnKeyUp) {
      keyUpOutline(e);
    }
    if (onKeyUp) {
      onKeyUp(e);
    }
  },
});

const getAccessibilityFunctionsForDayCard = (number, length, slidesToShow) => ({
  onKeyUp: (e) => dayCardKeyPressNavigate(e, number, length),
  onBlur: (e) => dayCardOutlineElement(e.target, false, number, length, slidesToShow),
});

const getScreenTier = () => {
  if (window.innerWidth < 576) {
    return 'extra-small';
  }
  if (window.innerWidth < 768) {
    return 'small';
  }
  if (window.innerWidth < 992) {
    return 'medium';
  }
  if (window.innerWidth < 1200) {
    return 'large';
  }
  return 'extra-large';
};

const getMediaQuery = (start, end) => {
  const getWidth = (tier) => {
    switch (tier) {
      case 'xl':
        return 1200;
      case 'lg':
        return 992;
      case 'md':
        return 768;
      case 'sm':
        return 576;
      default:
        return 0;
    }
  };
  const query = {
    minWidth: getWidth(start),
  };
  if (end) {
    query.maxWidth = getWidth(end) - 1;
  }
  return query;
};

const getGridTier = (grids, tier) => grids.find((grid) => grid.tier === tier);

const handleCmsLink = (history) => (e) => {
  const href = e.target.getAttribute('href');
  if (history && href && href.startsWith('/')) {
    e.preventDefault();
    history.push(href);
  }
};

const requiredLabel = (label) => {
  if (label[0] === '*') {
    return label;
  }
  return `*${label}`;
};

const humanReadableDuration = (duration, longFormat = false) => {
  const hours = Math.floor(duration);
  const minutes = Math.round((duration - hours) * 60);
  if (longFormat) {
    const h = hours > 1 ? `${hours} hours` : `${hours} hour`;
    const m = minutes > 1 ? `${minutes} minutes` : `${minutes} minute`;
    if (hours === 0) {
      return m;
    }
    if (minutes === 0) {
      return h;
    }
    return `${h} and ${m}`;
  }
  if (hours === 0) {
    return `${minutes}m`;
  }
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
};

export {
  COMMON_CONSTANTS,
  dayCardKeyPressNavigate,
  dayCardOutlineElement,
  getAccessibilityFunctions,
  getAccessibilityFunctionsForDayCard,
  getGridTier,
  getMediaQuery,
  getScreenTier,
  handleCmsLink,
  humanReadableDuration,
  keyUpOutline,
  LinkOrAnchor,
  outlineElement,
  outlineElementForSort,
  prepareHtml,
  purifyDom,
  requiredLabel,
  scrollToElement,
  TIERS,
  useWindowSize,
};
